import { useCallback } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  isOnePlan,
} from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type UseVariantSelectorLogicProps = {
  addon: Plan
  crossSellingAddons?: Plan[]
  availablePlans: Plan[]
  onSelectAddon: (plan: Plan) => void
  setSelectedAddonId: (id: string) => void
  installedAddonInternalId?: string | null
}

const getDefaultVariant = (plan: Plan): VariantsEnum | undefined => {
  if (plan.variantTypes.includes(ENTERPRISE_VARIANT)) return ENTERPRISE_VARIANT
  if (plan.variantTypes.includes(BUSINESS_VARIANT)) return BUSINESS_VARIANT
  return undefined
}

const matchesInstalledAddonId = (installedAddonId: string, planIds: string[]): boolean => {
  const normalizedInstalledAddonId = installedAddonId.toLowerCase().trim()

  return planIds.some((planId) => {
    const normalizedPlanId = planId.toLowerCase().trim()

    return (
      normalizedPlanId === normalizedInstalledAddonId ||
      normalizedPlanId.includes(normalizedInstalledAddonId) ||
      normalizedInstalledAddonId.includes(normalizedPlanId)
    )
  })
}

export const useVariantSelectorLogic = ({
  addon,
  crossSellingAddons,
  availablePlans,
  onSelectAddon,
  setSelectedAddonId,
  installedAddonInternalId,
}: UseVariantSelectorLogicProps) => {
  const {
    configPlan,
    setConfigPlan,
    period,
    currency,
    discount,
    setSeats,
    seats,
    currentSeats,
    isExpenses,
    allCrossSellingAddons,
    allMandatoryCrossSellingAddons,
    isOneInstalled,
  } = useSelfUpsellContext()

  const isRecruitment = addon.internalTitle.toLowerCase().includes('recruitment')
  const isOne = isOnePlan(addon.internalTitle)
  const tieredPrice = addon.pricing.getTieredPrice(period, seats)
  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit

  const getMandatoryCrossSellingAddonsIds = useCallback(() => {
    return allMandatoryCrossSellingAddons.f25Addons
      .filter((crossSellingAddon) => crossSellingAddon.isSelected)
      .map((crossSellingAddon) => crossSellingAddon.pricing.getPlanId(period) || '')
  }, [allMandatoryCrossSellingAddons.f25Addons, period])

  const isVariantAvailable = (variant: VariantsEnum) => addon.variantTypes.includes(variant)

  const recruitmentOptions = availablePlans.map((planAddon) => ({
    label: planAddon.title,
    value: planAddon.internalTitle,
  }))

  const installedOnePlan = availablePlans.find((planAddon) => {
    if (!installedAddonInternalId) return false

    const planIds = [
      planAddon.pricing.getPlanId(period),
      ...planAddon.pricing.getAllPlanIds(period),
    ].filter((id): id is string => Boolean(id))

    return matchesInstalledAddonId(installedAddonInternalId, planIds)
  })

  const oneSelectorOptions = availablePlans.map((planAddon) => {
    const isCurrentPlan = installedOnePlan?.internalTitle === planAddon.internalTitle

    return {
      label: isCurrentPlan
        ? `${planAddon.title} (${i18n.t('marketplace.revamp.in_your_plan')})`
        : planAddon.title,
      value: planAddon.internalTitle,
      disabled: isCurrentPlan,
    }
  })

  const selectableOnePlans = availablePlans.filter(
    (planAddon) => !installedOnePlan || planAddon.internalTitle !== installedOnePlan.internalTitle
  )

  const handleVariantChange = (variant: VariantsEnum) => {
    onSelectAddon(addon)

    addon.setActiveVariant(variant)

    const crossSellingAddonsIds = allCrossSellingAddons.f25Addons
      .filter((crossSellingAddon) => crossSellingAddon.isSelected)
      .map((crossSellingAddon) => crossSellingAddon.pricing.getPlanId(period) || '')

    const addonIds = isExpenses()
      ? addon.pricing.getAllPlanIds(period) || []
      : [addon.pricing.getPlanId(period) || '']

    setConfigPlan({
      addon: [...addonIds, ...crossSellingAddonsIds, ...getMandatoryCrossSellingAddonsIds()],
      variant,
    })
  }

  const handlePlanChange = useCallback(
    (selectedId: string) => {
      const selectedAddon = availablePlans.find(
        (planAddon) => planAddon.internalTitle === selectedId
      )
      if (!selectedAddon) return

      const defaultVariant = getDefaultVariant(selectedAddon)
      if (!defaultVariant) return

      onSelectAddon(selectedAddon)

      selectedAddon.setActiveVariant(defaultVariant)

      setConfigPlan({
        addon: [
          selectedAddon.pricing.getPlanId(period) || '',
          ...getMandatoryCrossSellingAddonsIds(),
        ],
        variant: defaultVariant,
      })
      setSelectedAddonId(selectedAddon.internalTitle)
    },
    [
      getMandatoryCrossSellingAddonsIds,
      period,
      availablePlans,
      onSelectAddon,
      setConfigPlan,
      setSelectedAddonId,
    ]
  )

  useEffect(() => {
    if (!isOne || !installedOnePlan) return
    if (addon.internalTitle !== installedOnePlan.internalTitle) return

    const nextPlan = selectableOnePlans[0]
    if (!nextPlan) return

    handlePlanChange(nextPlan.internalTitle)
  }, [
    addon.internalTitle,
    installedOnePlan?.internalTitle,
    isOne,
    selectableOnePlans[0]?.internalTitle,
  ])

  useEffect(() => {
    const hasEnterprise = addon.variantTypes.includes(ENTERPRISE_VARIANT)
    const hasBusiness = addon.variantTypes.includes(BUSINESS_VARIANT)
    if (!hasEnterprise && !hasBusiness) return

    if (configPlan.variant && addon.variantTypes.includes(configPlan.variant)) return
    onSelectAddon(addon)

    const nextVariant = hasEnterprise ? ENTERPRISE_VARIANT : BUSINESS_VARIANT
    addon.setActiveVariant(nextVariant)

    if (isExpenses()) {
      const defaultTieredPrice = addon
        .getSpecificVariant(nextVariant)
        ?.pricing.getTieredPrice(period, seats)
      setSeats(defaultTieredPrice?.tiers?.[0]?.endingUnit || 1)
    }

    const addons = [...addon.pricing.getAllPlanIds(period)]
    const crossSellingPlanId = crossSellingAddons
      ? crossSellingAddons
          .map((crossSellingAddon) => crossSellingAddon.pricing.getPlanId(period) || '')
          .filter((crossSellingId) => crossSellingId.includes('one'))
      : []

    if (crossSellingPlanId && !isOneInstalled()) {
      addons.push(...crossSellingPlanId)
    }

    const mandatoryCrossSellingAddonsIds = getMandatoryCrossSellingAddonsIds()
    if (mandatoryCrossSellingAddonsIds.length > 0) {
      addons.push(...mandatoryCrossSellingAddonsIds)
    }

    setConfigPlan({
      addon: addons,
      variant: nextVariant,
    })
  }, [configPlan.variant, period, seats])

  const handleExpensesSeatsChange = (value: number) => {
    setSeats(value)

    setConfigPlan({
      addon: [
        ...(addon.pricing.getAllPlanIds(period) || []),
        ...getMandatoryCrossSellingAddonsIds(),
      ],
      variant: configPlan.variant,
    })

    setSelectedAddonId(addon.internalTitle)
  }

  return {
    configPlan,
    period,
    currency,
    discount,
    seats,
    currentSeats,
    isExpenses,
    isRecruitment,
    isOne,
    installedOnePlan,
    includedSeatsPerModule,
    recruitmentOptions,
    oneSelectorOptions,
    isVariantAvailable,
    handleVariantChange,
    handlePlanChange,
    handleExpensesSeatsChange,
  }
}
